import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateClientSecretModel,
    CreateClientSecretResponse,
    GetClientSecretByIdResponse,
    GetClientSecretsByClientIdResponse,
    ProblemDetails,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns client secrets by their clientId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientSecretsByClientIdHook = () => {
    const getClientSecretsByClientId = useCustomIDSClient<GetClientSecretsByClientIdResponse>();

    return useCallback(
        (clientId: string, signal?: AbortSignal) => {
            return getClientSecretsByClientId({ url: `/clients/${clientId}/secrets`, method: 'GET', signal });
        },
        [getClientSecretsByClientId],
    );
};

export const getGetClientSecretsByClientIdQueryKey = (clientId: string) => {
    return [`/clients/${clientId}/secrets`] as const;
};

export const useGetClientSecretsByClientIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientSecretsByClientIdQueryKey(clientId);

    const getClientSecretsByClientId = useGetClientSecretsByClientIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>> = ({
        signal,
    }) => getClientSecretsByClientId(clientId, signal);

    return { queryKey, queryFn, enabled: !!clientId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientSecretsByClientIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>
>;
export type GetClientSecretsByClientIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientSecretsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientSecretsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientSecretsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientSecretsByClientId<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretsByClientIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientSecretsByClientIdQueryOptions(clientId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new client secret for the given clientId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useCreateClientSecretHook = () => {
    const createClientSecret = useCustomIDSClient<CreateClientSecretResponse>();

    return useCallback(
        (clientId: string, createClientSecretModel: CreateClientSecretModel, signal?: AbortSignal) => {
            return createClientSecret({
                url: `/clients/${clientId}/secrets`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createClientSecretModel,
                signal,
            });
        },
        [createClientSecret],
    );
};

export const useCreateClientSecretMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>,
        TError,
        { clientId: string; data: CreateClientSecretModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>,
    TError,
    { clientId: string; data: CreateClientSecretModel },
    TContext
> => {
    const mutationKey = ['createClientSecret'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createClientSecret = useCreateClientSecretHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>,
        { clientId: string; data: CreateClientSecretModel }
    > = (props) => {
        const { clientId, data } = props ?? {};

        return createClientSecret(clientId, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateClientSecretMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>
>;
export type CreateClientSecretMutationBody = CreateClientSecretModel;
export type CreateClientSecretMutationError = ErrorType<ProblemDetails>;

export const useCreateClientSecret = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>,
            TError,
            { clientId: string; data: CreateClientSecretModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateClientSecretHook>>>,
    TError,
    { clientId: string; data: CreateClientSecretModel },
    TContext
> => {
    return useMutation(useCreateClientSecretMutationOptions(options), queryClient);
};
/**
 * Returns a client secret by its clientSecretId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.read
 */
export const useGetClientSecretByIdHook = () => {
    const getClientSecretById = useCustomIDSClient<GetClientSecretByIdResponse>();

    return useCallback(
        (clientSecretId: string, signal?: AbortSignal) => {
            return getClientSecretById({ url: `/clients/secrets/${clientSecretId}`, method: 'GET', signal });
        },
        [getClientSecretById],
    );
};

export const getGetClientSecretByIdQueryKey = (clientSecretId: string) => {
    return [`/clients/secrets/${clientSecretId}`] as const;
};

export const useGetClientSecretByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientSecretId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetClientSecretByIdQueryKey(clientSecretId);

    const getClientSecretById = useGetClientSecretByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>> = ({ signal }) =>
        getClientSecretById(clientSecretId, signal);

    return { queryKey, queryFn, enabled: !!clientSecretId, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetClientSecretByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>
>;
export type GetClientSecretByIdQueryError = ErrorType<ProblemDetails>;

export function useGetClientSecretById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientSecretId: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientSecretById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientSecretId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetClientSecretById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientSecretId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetClientSecretById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    clientSecretId: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetClientSecretByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetClientSecretByIdQueryOptions(clientSecretId, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Deletes a client secret by its clientSecretId.<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/clients.write
 */
export const useDeleteClientSecretHook = () => {
    const deleteClientSecret = useCustomIDSClient<void>();

    return useCallback(
        (clientSecretId: string, signal?: AbortSignal) => {
            return deleteClientSecret({ url: `/clients/secrets/${clientSecretId}`, method: 'DELETE', signal });
        },
        [deleteClientSecret],
    );
};

export const useDeleteClientSecretMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>,
        TError,
        { clientSecretId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>,
    TError,
    { clientSecretId: string },
    TContext
> => {
    const mutationKey = ['deleteClientSecret'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteClientSecret = useDeleteClientSecretHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>,
        { clientSecretId: string }
    > = (props) => {
        const { clientSecretId } = props ?? {};

        return deleteClientSecret(clientSecretId);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteClientSecretMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>
>;

export type DeleteClientSecretMutationError = ErrorType<ProblemDetails>;

export const useDeleteClientSecret = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>,
            TError,
            { clientSecretId: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteClientSecretHook>>>,
    TError,
    { clientSecretId: string },
    TContext
> => {
    return useMutation(useDeleteClientSecretMutationOptions(options), queryClient);
};
